import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { getApiUrl, fetchWithAuth } from '../../../api'

interface EventListItem {
  event_id: number
  author_id: number | null
  title: string
  created_at: string
  thumbnail_url: string | null
}

export default function AdminEventList() {
  const navigate = useNavigate()
  const [events, setEvents] = useState<EventListItem[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchEvents()
  }, [])

  const fetchEvents = async () => {
    setLoading(true)
    try {
      const res = await fetchWithAuth(getApiUrl('/marubit/api/event/list?skip=0&limit=100'))
      if (!res.ok) throw new Error('조회 실패')
      const data = await res.json()
      setEvents(data.events)
    } catch {
      alert('목록을 불러오는 데 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: number, title: string) => {
    if (!window.confirm(`"${title}" 행사를 삭제하시겠습니까?`)) return
    try {
      const token = localStorage.getItem('access_token')
      const res = await fetch(getApiUrl(`/marubit/api/event/admin/${id}`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error('삭제 실패')
      fetchEvents()
    } catch {
      alert('삭제에 실패했습니다.')
    }
  }

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
    })

  if (loading) return (
    <div className="min-h-[60vh] flex items-center justify-center text-gray-300">
      불러오는 중...
    </div>
  )

  return (
    <div className="bg-[#f5f2f0] min-h-screen p-8">
      <div className="max-w-[1200px] mx-auto">

        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-[22px] font-extrabold text-[#2a1f1a] mb-1">행사 관리</h1>
            <p className="text-[13px] text-gray-400">
              {new Date().toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' })}
            </p>
          </div>
          <button
            onClick={() => navigate('/admin/event/new')}
            className="bg-[#887267] text-white text-[13px] font-semibold px-4 py-2 rounded-lg border-none cursor-pointer hover:bg-[#6b5248] transition-colors"
          >
            + 행사 추가
          </button>
        </div>

        <div className="bg-white rounded-2xl border border-[#e8e0dc] overflow-hidden shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
          <table className="w-full">
            <thead>
              <tr className="bg-[#ede0d8] border-b border-[#e8e0dc]">
                <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[48px]">ID</th>
                <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[64px]">썸네일</th>
                <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium">제목</th>
                <th className="hidden xl:table-cell text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[110px]">등록일</th>
                <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[60px]"></th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-12 text-gray-300 text-[13px]">
                    등록된 행사가 없습니다.
                  </td>
                </tr>
              ) : events.map(event => (
                <tr
                  key={event.event_id}
                  className="border-t border-[#f5f2f0] hover:bg-[#fdf8f0] transition-colors cursor-pointer"
                  onClick={() => navigate(`/admin/event/${event.event_id}`)}
                >
                  <td className="px-4 py-3 text-[13px] text-gray-400 whitespace-nowrap">{event.event_id}</td>
                  <td className="px-4 py-3">
                    {event.thumbnail_url ? (
                      <img src={event.thumbnail_url} alt="썸네일" className="w-10 h-10 object-cover rounded-lg border border-[#e8e0dc]" />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-[#f5f2f0] border border-[#e8e0dc]" />
                    )}
                  </td>
                  <td className="px-4 py-3 text-[13px] font-medium text-[#2a1f1a]">{event.title}</td>
                  <td className="hidden xl:table-cell px-4 py-3 text-[12px] text-gray-400 whitespace-nowrap">{formatDate(event.created_at)}</td>
                  <td className="px-4 py-3 whitespace-nowrap" onClick={e => e.stopPropagation()}>
                    <button
                      onClick={() => handleDelete(event.event_id, event.title)}
                      className="text-[12px] text-red-400 hover:text-red-600 border border-red-100 rounded px-2 py-1 bg-transparent cursor-pointer whitespace-nowrap"
                    >
                      삭제
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}